import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { ProductCard } from "../components/ProductCard";
import { useCategories } from "../context/CategoryContext";

const Shop = () => {
  const { t, lang } = useLanguage();
  const { groups, label, childrenOf, allCategories } = useCategories();
  const [params, setParams] = useSearchParams();

  const category = params.get("category") || "";
  const group = params.get("group") || "";
  const search = params.get("search") || "";
  const sort = params.get("sort") || "newest";

  /* Siyahı KEŞLƏNİR (react-query onsuz da qurulub, `staleTime` 60 san).
     Səbəb sürət deyil, GERİ QAYIDIŞDIR: keşsiz halda müştəri məhsuldan
     siyahıya qayıdanda ekran əvvəlcə boşalır, səhifə qısalır, sonra məhsullar
     gəlir — qaldığı yerə qayıtmaq ya alınmır, ya da gözlə görünən sıçrayışla
     olur. Keşdən gələn siyahı DƏRHAL çəkilir, yəni səhifə elə ilk kadrda
     lazımi hündürlükdədir. */
  const { data: products = [], isPending: loading } = useQuery({
    queryKey: ["products", { category, group, search, sort }],
    queryFn: () => {
      const q = {};
      if (category) q.category = category;
      if (group) q.group = group;
      if (search) q.search = search;
      if (sort) q.sort = sort;
      return api.get("/products", { params: q }).then(({ data }) => data);
    },
  });

  const setCategory = (c) => {
    const next = new URLSearchParams(params);
    if (c) next.set("category", c); else next.delete("category");
    next.delete("group");
    next.delete("search");
    if (sort && sort !== "newest") next.set("sort", sort);
    setParams(next);
  };

  const setSort = (v) => {
    const next = new URLSearchParams(params);
    if (v && v !== "newest") next.set("sort", v);
    else next.delete("sort");
    setParams(next);
  };

  const setGroup = (g) => {
    const next = new URLSearchParams();
    if (g) next.set("group", g);
    if (sort && sort !== "newest") next.set("sort", sort);
    setParams(next);
  };

  const title = search
    ? `"${search}"`
    : category
    ? label(category, lang)
    : group
    ? label(group, lang)
    : t("nav_all");

  // Qrup seçilibsə onun alt kateqoriyaları, seçilməyibsə hamısı.
  const chips = group ? childrenOf(group) : allCategories;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16" data-testid="shop-page">
      <div className="mb-12">
        <p className="overline mb-3">Sparkle / {t("nav_all")}</p>
        <h1 className="font-serif font-light text-5xl sm:text-6xl tracking-tight" data-testid="shop-title">{title}</h1>
      </div>

      {/* Group filter */}
      <div className="flex flex-wrap gap-3 mb-6">
        <button onClick={() => setGroup("")} className={`overline px-4 py-2 border transition-colors ${!group && !category ? "bg-brand-ink text-white border-brand-ink" : "border-brand-line hover:border-brand-ink"}`} data-testid="filter-group-all">
          {t("all_categories")}
        </button>
        {groups.map((g) => (
          <button
            key={g.key}
            onClick={() => setGroup(g.key)}
            className={`overline px-4 py-2 border transition-colors ${group === g.key ? "bg-brand-ink text-white border-brand-ink" : "border-brand-line hover:border-brand-ink"}`}
            data-testid={`filter-group-${g.key}`}
          >
            {label(g.key, lang)}
          </button>
        ))}
      </div>

      {/* Kateqoriyalar + sıralama.
          Telefonda ŞAQULİ düzülürdü: çiplər `flex-1` idi, yanındakı sıralama
          seçicisi isə dar ekranda demək olar bütün eni tuturdu — çiplərə nazik
          sütun qalır və hər biri öz sətrinə düşürdü. Nəticədə məhsullar
          ekrandan çox aşağıda qalırdı.
          İndi telefonda: çiplər BİR sətirdə, yana sürüşür (18 kateqoriya
          sarınsaydı 5 sətir tutardı); sıralama isə altda ayrıca sətirdədir.
          `sm`-dən yuxarı köhnə görünüş qalır. */}
      <div className="flex flex-col gap-4 mb-14 border-t border-brand-line pt-6 sm:flex-row sm:items-start sm:justify-between">
        {/* `-mx-6` lenti səhifənin kənarlarına çıxarır ki, sürüşmə kəsik görünməsin. */}
        <div className="-mx-6 px-6 overflow-x-auto no-scrollbar sm:mx-0 sm:px-0 sm:overflow-visible sm:flex-1">
        <div className="flex gap-2 w-max sm:w-auto sm:flex-wrap">
        {chips.map((c) => (
          <button
            key={c.key}
            onClick={() => setCategory(category === c.key ? "" : c.key)}
            data-testid={`filter-cat-${c.key}`}
            className={`shrink-0 whitespace-nowrap text-xs tracking-wider px-3 py-1.5 border transition-colors ${category === c.key ? "bg-brand-ink text-white border-brand-ink" : "border-brand-line text-brand-sub hover:border-brand-ink hover:text-brand-ink"}`}
          >
            {label(c.key, lang)}
          </button>
        ))}
        </div>
        </div>

        {/* Sıralama — geniş ekranda sağ küncdə, telefonda altda */}
        <div className="flex items-center gap-2 shrink-0">
          <label htmlFor="sort" className="overline text-brand-muted whitespace-nowrap">
            {t("sort_by")}
          </label>
          <select
            id="sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            data-testid="sort-select"
            className="border border-brand-line bg-transparent text-xs tracking-wider px-3 py-1.5 outline-none focus:border-brand-ink transition-colors cursor-pointer"
          >
            <option value="newest">{t("sort_newest")}</option>
            <option value="price_asc">{t("sort_price_asc")}</option>
            <option value="price_desc">{t("sort_price_desc")}</option>
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-brand-muted">{t("loading")}</p>
      ) : products.length === 0 ? (
        <p className="text-brand-muted py-20 text-center" data-testid="no-products">{t("no_products")}</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-16 gap-x-8">
          {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
        </div>
      )}
    </div>
  );
};

export default Shop;
